import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useUpdateLeadStatusMutation,
  useAssignLeadToUserMutation,
  useDeleteLeadMutation,
  useLazyGetLeadAssignedUsersQuery,
  useLazyGetLeadAttachmentsQuery,
  useDownloadLeadFileMutation,
  useCreateCustomerFromLeadMutation,
  useCreateShareableLinkMutation,
  useCreateIssueMutation,
} from '../../../store/api';
import SweetAlert from '../../../utils/sweetAlert';

import {
  toDatetimeLocalFromApi,
  clampDatetimeLocal,
  clampDateLocal,
  nowDateLocal,
  getMinEndDatetimeLocal,
} from '../../../utils/datetimeLocal';

import {
  resolveLeadStatusId,
  resolveLeadStatusName,
  resolveLastStatusLog,
  matchOptionId as matchStatusId,
} from '../leadFormHelpers';

export const useLeadActions = (leadsData, permissions = {}) => {
  const navigate = useNavigate();
  const {
    canChangeLeadStatus = true,
    canAssignLeads = true,
    canDeleteLeads = true,
    canCreateShareableLink = true,
  } = permissions;

  const [updateLeadStatus] = useUpdateLeadStatusMutation();
  const [assignLeadToUser] = useAssignLeadToUserMutation();
  const [deleteLead] = useDeleteLeadMutation();
  const [fetchAssignedUsersTrigger] = useLazyGetLeadAssignedUsersQuery();
  const [fetchAttachmentsTrigger] = useLazyGetLeadAttachmentsQuery();
  const [downloadLeadFile] = useDownloadLeadFileMutation();
  const [createCustomerFromLead] = useCreateCustomerFromLeadMutation();
  const [createShareableLink] = useCreateShareableLinkMutation();
  const [createIssue] = useCreateIssueMutation();

  const [dialogs, setDialogs] = useState({
    status: false,
    assign: false,
    share: false,
    attachments: false,
    assignedUsers: false,
    issue: false,
    createCustomer: false,
  });
  const [statusData, setStatusData] = useState({
    newStatus: '',
    conditionalData: {
      date: '',
      description: '',
      // assignee_id: '',
      assignees: [],
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
    },
  });
  const [assignData, setAssignData] = useState({ selectedUsers: [] });
  const [shareData, setShareData] = useState({ url: '', password: '' });
  const [shareLoading, setShareLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedUsersLoading, setAssignedUsersLoading] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const handleViewLead = (id) => navigate(`/leads/view/${id}`);
  const handleEditLead = (id) => navigate(`/leads/edit/${id}`);
  const handleCreateLead = () => navigate('/leads/create');
  const handleCreateDba = (leadId) => navigate(`/create-merchant?lead_id=${leadId}`);
  const handleEditDba = (leadId) => navigate(`/create-merchant?edit=true&id=${leadId}`);

  const openDialog = (name) => setDialogs((p) => ({ ...p, [name]: true }));
  const closeDialog = (name) => setDialogs((p) => ({ ...p, [name]: false }));

  const handleOpenIssueDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('issue');
  };

  const handleCloseIssueDialog = () => closeDialog('issue');

  const handleOpenCreateCustomerDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('createCustomer');
  };

  const handleCloseCreateCustomerDialog = () => closeDialog('createCustomer');

  const handleCloseCredentialsDialog = () => {
    setShowCredentialsDialog(false);
    setCreatedCustomer(null);
  };

  const handleCreateCustomer = async (formData) => {
    try {
      if (!selectedLead) {
        toast.error('No lead selected');
        return;
      }
      leadsData.setProcessingId(selectedLead.id);
      const payload = { ...formData, lead_id: selectedLead.id };
      const res = await createCustomerFromLead(payload).unwrap();

      if (res?.success) {
        const credentials = {
          email: formData.email,
          password: formData.password,
          name: formData.name || selectedLead.name,
        };
        setCreatedCustomer(credentials);
        handleCloseCreateCustomerDialog();
        setShowCredentialsDialog(true);
        toast.success('Customer added successfully!');
      } else {
        toast.error(res?.message || 'Failed to add customer');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to add customer');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleCreateIssue = async (issueData) => {
    try {
      leadsData.setProcessingId(selectedLead.id);
      const res = await createIssue(issueData).unwrap();
      if (res?.success) {
        toast.success('Issue added successfully');
        handleCloseIssueDialog();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to add issue');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleOpenStatusDialog = (lead) => {
    setSelectedLead(lead);
    const rawStatusId = resolveLeadStatusId(lead);
    const newStatus = matchStatusId(leadsData.statusOptions, rawStatusId);

    const log = resolveLastStatusLog(lead);
    const meeting = log?.meeting;
    const statusName = resolveLeadStatusName(lead);

    const assigneeIdsFromLead =
      lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [];

    const conditionalData = {
      date: '',
      description: '',
      assignees: [],
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
    };

    if (statusName === 'Meeting' && meeting) {
      const start = clampDatetimeLocal(toDatetimeLocalFromApi(meeting.start_date_time));
      conditionalData.assignees = assigneeIdsFromLead;
      conditionalData.timeZone = meeting.time_zone || 'UTC';
      conditionalData.startDateTime = start;
      conditionalData.endDateTime = clampDatetimeLocal(
        toDatetimeLocalFromApi(meeting.end_date_time),
        getMinEndDatetimeLocal(start)
      );
      conditionalData.meetingDescription = meeting.description || '';
    } else if (log && statusName && statusName !== 'Meeting') {
      conditionalData.date = clampDateLocal(
        log.date || log.created_at?.slice?.(0, 10) || '',
        nowDateLocal()
      );
      conditionalData.description = log.description || '';
    }

    setStatusData({
      newStatus,
      conditionalData,
    });
    openDialog('status');
  };

  const handleCloseStatusDialog = () => {
    closeDialog('status');
    setStatusData({
      newStatus: '',
      conditionalData: {
        date: '',
        description: '',
        assignees: [],
        timeZone: 'UTC',
        startDateTime: '',
        endDateTime: '',
        meetingDescription: '',
      },
    });
  };

  const handleOpenAssignDialog = (lead) => {
    setSelectedLead(lead);
    const existingIds = lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [];
    setAssignData({ selectedUsers: existingIds.length ? existingIds : [] });
    openDialog('assign');
  };

  const handleCloseAssignDialog = () => {
    closeDialog('assign');
    setAssignData({ selectedUsers: [] });
  };

  const handleOpenShareDialog = async (lead) => {
    setSelectedLead(lead);
    openDialog('share');
    await generateShareableLink(lead.id);
  };

  const handleCloseShareDialog = () => {
    closeDialog('share');
    setShareData({ url: '', password: '' });
  };

  const handleOpenAttachmentsDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('attachments');
    fetchAttachments(lead.id);
  };

  const handleCloseAttachmentsDialog = () => {
    closeDialog('attachments');
    setAttachments([]);
  };

  const handleOpenAssignedUsersDialog = (lead) => {
    setSelectedLead(lead);
    openDialog('assignedUsers');
    fetchAssignedUsers(lead.id);
  };

  const handleCloseAssignedUsersDialog = () => {
    closeDialog('assignedUsers');
    setAssignedUsers([]);
  };

  const handleStatusChange = async () => {
    if (!canChangeLeadStatus) {
      toast.error('You do not have permission to change lead status.');
      return;
    }
    if (!statusData.newStatus) {
      toast.warn('Select a status');
      return;
    }

    const statusObj = leadsData.statusOptions.find((s) => s.id === statusData.newStatus);
    const statusName = statusObj?.name;
    const payload = {
      status_id: statusData.newStatus,
      date: statusData.conditionalData.date,
      description: statusData.conditionalData.description,
    };

    if (statusName === 'Select') {
      if (!statusData.conditionalData.date || !statusData.conditionalData.description) {
        toast.warn('Date & description are required');
        return;
      }
    }

    if (statusName === 'Meeting') {
      if (
        !statusData.conditionalData.assignees?.length ||
        !statusData.conditionalData.startDateTime ||
        !statusData.conditionalData.endDateTime ||
        !statusData.conditionalData.meetingDescription
      ) {
        toast.warn('All meeting fields are required');
        return;
      }
      Object.assign(payload, {
        // assignee_id: statusData.conditionalData.assignee_id,
        assignees: statusData.conditionalData.assignees,
        time_zone: statusData.conditionalData.timeZone,
        start_date_time: statusData.conditionalData.startDateTime,
        end_date_time: statusData.conditionalData.endDateTime,
        description: statusData.conditionalData.meetingDescription,
      });
    }

    try {
      leadsData.setProcessingId(selectedLead.id);
      const res = await updateLeadStatus({ leadId: selectedLead.id, payload }).unwrap();
      if (res?.success) {
        leadsData.fetchLeads();
        toast.success('Status updated');
        handleCloseStatusDialog();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to update status');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleAssign = async () => {
    if (!canAssignLeads) {
      toast.error('You do not have permission to assign leads.');
      return;
    }
    if (!assignData.selectedUsers?.length) {
      toast.warn('Select at least one user');
      return;
    }
    try {
      leadsData.setProcessingId(selectedLead.id);
      const userIds = assignData.selectedUsers;
      await assignLeadToUser({ leadId: selectedLead.id, userIds }).unwrap();
      const users = userIds
        .map((id) => leadsData.usersList.find((u) => u.id === id))
        .filter(Boolean);
      leadsData.setLeads((prev) =>
        prev.map((lead) =>
          lead.id === selectedLead.id
            ? { ...lead, assignees: users }
            : lead
        )
      );
      toast.success('Lead assigned');
      handleCloseAssignDialog();
    } catch (err) {
      toast.error(err?.message || 'Failed to assign lead');
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!canDeleteLeads) {
      toast.error('You do not have permission to delete leads.');
      return;
    }
    try {
      const result = await SweetAlert.deleteConfirm(lead.name || 'this lead');
      if (!result.isConfirmed) return;

      leadsData.setProcessingId(lead.id);
      await deleteLead(lead.id).unwrap();
      await SweetAlert.success({ text: 'Lead deleted successfully' });
      leadsData.fetchLeads();
    } catch (error) {
      console.error('Delete lead error:', error);
      await SweetAlert.error({ text: 'Failed to delete lead' });
    } finally {
      leadsData.setProcessingId(null);
    }
  };

  const generateShareableLink = async (leadId) => {
    if (!canCreateShareableLink) {
      toast.error('You do not have permission to add shareable links.');
      return;
    }
    if (!leadId) return;
    try {
      setShareLoading(true);
      const res = await createShareableLink(leadId).unwrap();
      const linkData = res?.data ?? res;
      if (linkData?.shareable_url) {
        setShareData({
          url: linkData.shareable_url,
          password: linkData.password || '',
        });
      } else if (res?.data?.data) {
        setShareData({
          url: res.data.data.shareable_url,
          password: res.data.data.password || '',
        });
      } else {
        toast.error(res?.message || 'No link returned');
        handleCloseShareDialog();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to add shareable link');
      handleCloseShareDialog();
    } finally {
      setShareLoading(false);
    }
  };

  const fetchAttachments = async (leadId) => {
    if (!leadId) return;
    try {
      setAttachmentsLoading(true);
      const res = await fetchAttachmentsTrigger(leadId).unwrap();
      const list =
        res?.attachments ?? res?.data?.attachments ?? res?.data?.data?.attachments ?? [];
      setAttachments(list);
    } catch (err) {
      toast.error(err?.message || 'Failed to load attachments');
      handleCloseAttachmentsDialog();
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchAssignedUsers = async (leadId) => {
    if (!leadId) return;
    try {
      setAssignedUsersLoading(true);
      const res = await fetchAssignedUsersTrigger(leadId).unwrap();
      const list = res?.data?.data?.status_logs ?? res?.data?.status_logs ?? res?.status_logs ?? [];
      setAssignedUsers(list);
    } catch (err) {
      toast.error(err?.message || 'Failed to load assigned users');
      handleCloseAssignedUsersDialog();
    } finally {
      setAssignedUsersLoading(false);
    }
  };

  const handleDownloadFile = async (file, index) => {
    const filePath = file.path;
    const fileName = file?.title || file?.file_name || file?.name || `File ${index + 1}`;

    if (!filePath) {
      toast.error('No file path');
      return;
    }
    try {
      setDownloadingIndex(index);
      const blobData = await downloadLeadFile(filePath).unwrap();
      const blob =
        blobData instanceof Blob
          ? blobData
          : new Blob([blobData], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${fileName} downloaded`);
    } catch (err) {
      toast.error(err?.message || 'Download failed');
    } finally {
      setDownloadingIndex(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied!');
    } catch {
      toast.error('Copy failed');
    }
  };

  const setNewStatus = (status) =>
    setStatusData((p) => ({ ...p, newStatus: status }));
  const setSelectedUsers = (userIds) =>
    setAssignData((p) => ({ ...p, selectedUsers: Array.isArray(userIds) ? userIds : [] }));

  const handleConditionalChange = (field, value) => {
    setStatusData((p) => ({
      ...p,
      conditionalData: { ...p.conditionalData, [field]: value },
    }));
  };

  return {
    dialogs,
    statusData,
    assignData,
    shareData,
    shareLoading,
    attachments,
    attachmentsLoading,
    downloadingIndex,
    assignedUsers,
    assignedUsersLoading,
    selectedLead,
    handleViewLead,
    handleEditLead,
    handleCreateLead,
    handleCreateDba,
    handleEditDba,
    handleOpenStatusDialog,
    handleCloseStatusDialog,
    handleOpenAssignDialog,
    handleCloseAssignDialog,
    handleOpenShareDialog,
    handleCloseShareDialog,
    handleOpenAttachmentsDialog,
    handleCloseAttachmentsDialog,
    handleOpenAssignedUsersDialog,
    handleCloseAssignedUsersDialog,
    handleStatusChange,
    handleAssign,
    handleDeleteLead,
    handleDownloadFile,
    copyToClipboard,
    setNewStatus,
    setSelectedUsers,
    handleConditionalChange,
    setSelectedLead,
    handleOpenIssueDialog,
    handleCloseIssueDialog,
    handleCreateIssue,
    handleOpenCreateCustomerDialog,
    handleCloseCreateCustomerDialog,
    handleCreateCustomer,
    showCredentialsDialog,
    setShowCredentialsDialog,
    createdCustomer,
    handleCloseCredentialsDialog,
  };
};
